'use client';

import {
  useWikiSource,
  findWikiPath,
  parseMarkdown,
  ArticleTopBlocks,
  ArticleBottomBlocks,
  ArticleSidebarBlocks,
  useArticleBlockGroups,
  type WikiPage,
  type WikiTreeNode,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { WikiTreeNav } from './WikiTreeNav';
import { WikiPageHeader } from './WikiPageHeader';
import { ContentModuleShell } from '@akademi/site-builder-runtime';

/**
 * Three-column docs reader: tree sidebar + body + optional TOC.
 * Merges per-page block overrides with the host's
 * {@code pageTemplate.blocks}.
 */
export interface WikiDetailLayoutProps {
  slug: string;
  config?: {
    showBreadcrumbs?: boolean;
    showLastUpdated?: boolean;
    expandTreeToDepth?: number;
    pageTemplate?: { blocks?: unknown[] };
  };
  pageHrefTemplate?: string;
  themeId?: string | null;
  /** Server-fetched page for SSR. When provided (with {@code tree}),
   *  the layout renders directly — no client WikiSource fetch. */
  page?: WikiPage | null;
  /** Server-fetched tree for SSR (breadcrumbs + sidebar nav). */
  tree?: WikiTreeNode[];
}

export function WikiDetailLayout({
  slug,
  config,
  pageHrefTemplate = '/wiki/{slug}',
  themeId,
  page: pageProp,
  tree: treeProp,
}: WikiDetailLayoutProps) {
  const wiki = useWikiSource();
  const ssr = pageProp !== undefined || treeProp !== undefined;

  const state = useAsyncResource(async () => {
    if (ssr) return null; // ignored when server data is provided
    if (!wiki) return null;
    const [page, tree] = await Promise.all([wiki.getPageBySlug(slug), wiki.getTree()]);
    if (!page) return null;
    return { page, tree, breadcrumbs: findWikiPath(tree, slug) };
  }, [wiki, slug, ssr]);

  // SSR path resolves synchronously from props; client path uses the resource.
  const resolved:
    | { page: WikiPage; tree: WikiTreeNode[]; breadcrumbs: WikiTreeNode[] }
    | null
    | undefined = ssr
    ? pageProp
      ? { page: pageProp, tree: treeProp ?? [], breadcrumbs: findWikiPath(treeProp ?? [], slug) }
      : null
    : state.kind === 'loaded'
      ? state.data
      : undefined;

  if (!ssr && !wiki) {
    return <AdapterPlaceholder blockType="WikiDetailLayout" adapterName="WikiSource" />;
  }
  if (resolved === undefined || resolved === null) return null;
  const { page, tree, breadcrumbs } = resolved;

  const groups = useArticleBlockGroups(page, config, 'wiki');
  const paragraphs = parseMarkdown(page.body);

  return (
    <ContentModuleShell
      title={page.title}
      sidebar={
        <WikiTreeNav
          tree={tree}
          activeSlug={page.slug}
          expandToDepth={config?.expandTreeToDepth ?? 1}
          pageHrefTemplate={pageHrefTemplate}
        />
      }
      aside={
        groups.sidebar.length > 0 ? (
          <ArticleSidebarBlocks
            article={page}
            config={config}
            kind="wiki"
            themeId={themeId ?? null}
          />
        ) : null
      }
    >
      <WikiPageHeader
        title={page.title}
        excerpt={page.excerpt}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={config?.showBreadcrumbs !== false}
        {...(config?.showLastUpdated !== false ? { lastUpdated: page.updatedAt } : {})}
        pageHrefTemplate={pageHrefTemplate}
      />
      <ArticleTopBlocks article={page} config={config} kind="wiki" themeId={themeId ?? null} />
      <article
        style={{
          fontSize: 'var(--font-size-md, 16px)',
          lineHeight: 1.75,
          color: 'var(--color-text-primary, #18181b)',
        }}
      >
        {paragraphs.map((b, i) => {
          if (b.type === 'h1')
            return (
              <h1
                key={i}
                style={{ fontSize: 'var(--font-size-2xl, 28px)', fontWeight: 700, marginTop: 32 }}
              >
                {b.text}
              </h1>
            );
          if (b.type === 'h2')
            return (
              <h2
                key={i}
                style={{ fontSize: 'var(--font-size-xl, 22px)', fontWeight: 600, marginTop: 24 }}
              >
                {b.text}
              </h2>
            );
          return (
            <p key={i} style={{ marginTop: 16 }}>
              {b.text}
            </p>
          );
        })}
      </article>
      <ArticleBottomBlocks article={page} config={config} kind="wiki" themeId={themeId ?? null} />
    </ContentModuleShell>
  );
}
